import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Button, Container, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectAssessmentName, selectControlCount } from '../../redux/reducers/assessmentReducer';
import { Criticality, selectFindingsList } from '../../redux/reducers/findingsReducer';
import { ContentTypes, downloadBlob, parseIntoCsv } from '../../utils/exportHelpers';
import AssessmentFinding from './AssessmentFinding';

function AssessmentFindings({ review = false }) {
  const { t } = useTranslation();
  const controlCount = useSelector(selectControlCount);
  const findings = useSelector(selectFindingsList);
  const assessmentName = useSelector(selectAssessmentName);
  const [showCriticality, setShowCriticality] = useState('all');
  const [countByCriticality, setCountByCriticality] = useState({
    major: 0,
    minor: 0,
    info: 0,
  });

  const handleSelectCriticality = (value) => {
    setShowCriticality(value);
  };

  useEffect(() => {
    const findingsByCriticality = Object.groupBy(findings, ({ criticality }) => criticality);
    const count = {
      major: findingsByCriticality.major?.length ?? 0,
      minor: findingsByCriticality.minor?.length ?? 0,
      info: findingsByCriticality.info?.length ?? 0,
    };
    setCountByCriticality(count);
  }, [findings]);

  const handleExportCsv = () => {
    const headerRow = [
      t('assessment.review.header.id'),
      t('assessment.review.header.title'),
      t('assessment.review.header.criticality'),
      t('assessment.review.header.description'),
      t('assessment.review.header.usual'),
      t('assessment.review.header.recommendation'),
      t('assessment.review.header.response'),
    ];
    const dataArray = findings.map((finding) => {
      const id = finding.controlId.split(':').slice(-1)[0];
      const usualFindings = finding.usualFindings
        .reduce((result, item) => (
          result === '' ? result.concat(`${t(`finding.usualFinding.${item}`)}`) : result.concat(`, ${t(`finding.usualFinding.${item}`)}`)
        ), '');
      const findingData = [
        id,
        finding.title,
        t(`finding.criticality.${finding.criticality}`),
        finding.description,
        usualFindings,
        finding.recommendation,
        finding.response,
      ];
      return findingData;
    });
    const csv = parseIntoCsv([headerRow, ...dataArray]);
    downloadBlob(csv, ContentTypes.CSV, `${assessmentName}_review.csv`);
  };

  return (
    <Container disableGutters>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{t('assessment.findingList.title')}</Typography>
          {review && (
            <Button
              id='export-to-csv'
              variant="outlined"
              endIcon={<FileDownloadIcon />}
              onClick={() => handleExportCsv()}
            >
              <Typography variant="button">{t('assessment.findingList.exportCsv')}</Typography>
            </Button>
          )}
        </Stack>
        <Container disableGutters>
          <Typography display="inline">
            {`${controlCount} ${t('assessment.findingList.totalControls')}`}
          </Typography>
          <Typography display="inline">
            {` ${t('assessment.findingList.inScope')} ${t('assessment.findingList.inTotal')}`}
          </Typography>
          <Typography display="inline">
            {` ${findings.length} ${t('assessment.findingList.totalFindings')} `}
          </Typography>
          <Typography display="inline">
            {t('assessment.findingList.findingsMade')}
          </Typography>
        </Container>
        <Stack direction="row" spacing={1}>
          {Criticality.list().map((criticality) => (
            <Button
              id={`filter-criticality-${criticality}`}
              variant={showCriticality === criticality ? 'contained' : 'outlined'}
              size="small"
              color="secondary"
              key={criticality}
              onClick={() => handleSelectCriticality(criticality)}
            >
              <Typography variant="buttonSmall">
                {`${t(`finding.criticality.${criticality}`)} (${countByCriticality[criticality]})`}
              </Typography>
            </Button>
          ))}
          <Button
            variant={showCriticality === 'all' ? 'contained' : 'outlined'}
            size="small"
            color="secondary"
            id="show-all"
            key="all"
            onClick={() => handleSelectCriticality('all')}
          >
            <Typography variant="buttonSmall">{t('assessment.findingList.showAll')}</Typography>
          </Button>
        </Stack>
        <Stack spacing={2} sx={{ px: 0.25 }}>
          {findings.map((finding) => (
            (showCriticality === 'all' || showCriticality === finding.criticality)
            && <AssessmentFinding key={finding.id} finding={finding} review={review} />
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}

export default AssessmentFindings;
